"use client";

import { useState, useEffect } from "react";
import { X, Tag } from "lucide-react";

interface TagOption {
    id: string;
    name: string;
    slug: string;
    category: string;
    color: string;
}

interface TagSelectProps {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
}

export default function TagSelect({ selectedIds, onChange }: TagSelectProps) {
    const [tags, setTags] = useState<TagOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTags = async () => {
            try {
                const response = await fetch("/api/tags");
                const data = await response.json();
                // Only set tags if response is an array
                if (Array.isArray(data)) {
                    setTags(data);
                }
            } catch (error) {
                console.error("Error fetching tags:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTags();
    }, []);

    const handleToggle = (tagId: string) => {
        if (selectedIds.includes(tagId)) {
            onChange(selectedIds.filter((id) => id !== tagId));
        } else {
            onChange([...selectedIds, tagId]);
        }
    };

    // Group tags by category
    const groupedTags = tags.reduce((acc, tag) => {
        if (!acc[tag.category]) {
            acc[tag.category] = [];
        }
        acc[tag.category].push(tag);
        return acc;
    }, {} as Record<string, TagOption[]>);

    const categoryLabels: Record<string, string> = {
        politician: "Politycy",
        party: "Partie",
        topic: "Tematy",
    };

    if (isLoading) {
        return (
            <div className="text-sm text-gray-500">Ładowanie tagów...</div>
        );
    }

    if (tags.length === 0) {
        return (
            <div className="text-sm text-gray-500">Brak dostępnych tagów</div>
        );
    }

    return (
        <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Tag size={16} />
                Tagi (opcjonalnie)
            </label>

            {Object.entries(groupedTags).map(([category, categoryTags]) => (
                <div key={category}>
                    <div className="text-xs text-gray-500 mb-2">
                        {categoryLabels[category] || category}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {categoryTags.map((tag) => {
                            const isSelected = selectedIds.includes(tag.id);
                            return (
                                <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => handleToggle(tag.id)}
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-all border ${isSelected
                                        ? "ring-2 ring-offset-1"
                                        : "opacity-60 hover:opacity-100"
                                        }`}
                                    style={{
                                        backgroundColor: `${tag.color}20`,
                                        color: tag.color,
                                        borderColor: `${tag.color}40`,
                                        ...(isSelected && { ringColor: tag.color }),
                                    }}
                                >
                                    {tag.name}
                                    {isSelected && <X size={14} />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}

            {selectedIds.length > 0 && (
                <div className="text-xs text-gray-500">
                    Wybrano: {selectedIds.length} {selectedIds.length === 1 ? "tag" : "tagów"}
                </div>
            )}
        </div>
    );
}
